import React, { useEffect, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import {
    Button,
    Chip,
    CircularProgress,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Tooltip,
    Typography,
} from '@material-ui/core';
import { Add, Block, Archive as ArchiveIcon } from '@material-ui/icons';
import { grey, green, orange, red, blueGrey } from '@material-ui/core/colors';

import { CustomTableTitle } from '../../../shared/components';
import { SVG_ICONS } from '../../../shared/icons/SvgIcons';
import { enqueueSnackbar, Toast, ToastError, ToastSuccess } from '../../../shared/components/Notifier';
import { hasAddPermission, hasPermission } from '../../../shared/lib/PermissionManager';
import { checkStatus, parseJSON, handleError } from '../../../shared/api/core';
import { PaginatedResponse } from '../../../shared/models';
import { DataTable, ColumnDef, SortConfig } from '../../components/DataTable';
import {
    archiveExpiringStockTracker,
    deactivateExpiringStockTracker,
    getExpiringStockTrackers,
    syncKoronaDeactivateExpiringStockTracker,
} from '../../api/Api';
import {
    EXPIRING_STOCK_DEACTIVATED_STATUSES,
    ExpiringStockPromoStatus,
    ExpiringStockTracker,
    ExpiringStockTrackerStatus,
} from '../../models';
import CreateTrackerDialog from './CreateTrackerDialog';

const PERMISSION_MODEL = 'expiringstocktracker';
const DEFAULT_PAGE_SIZE = 25;

const STATUS_LABELS: Record<ExpiringStockTrackerStatus, string> = {
    ACTIVE: 'Active',
    DEACTIVATED_STOCK_DEPLETED: 'Deactivated - Stock Depleted',
    DEACTIVATED_EXPIRED: 'Deactivated - Expired',
    DEACTIVATED_MANUAL: 'Deactivated - Manual',
    ARCHIVED: 'Archived',
};

const STATUS_COLORS: Record<ExpiringStockTrackerStatus, string> = {
    ACTIVE: green[600],
    DEACTIVATED_STOCK_DEPLETED: orange[700],
    DEACTIVATED_EXPIRED: orange[900],
    DEACTIVATED_MANUAL: red[600],
    ARCHIVED: blueGrey[400],
};

const PROMO_STATUS_LABELS: Record<ExpiringStockPromoStatus, string> = {
    NONE: 'No Promo',
    ACTIVE: 'Active',
    DEACTIVATED_KONNECT: 'Deactivated - Konnect',
    DEACTIVATED_KORONA: 'Deactivated - Korona',
};

const PROMO_STATUS_COLORS: Record<ExpiringStockPromoStatus, string> = {
    NONE: grey[500],
    ACTIVE: green[600],
    DEACTIVATED_KONNECT: red[600],
    DEACTIVATED_KORONA: orange[700],
};

const StatusChip: React.FC<{ label: string; color: string }> = ({ label, color }) => (
    <Tooltip title={label}>
        <div style={{ maxWidth: '100%', minWidth: 0 }}>
            <Chip
                label={label}
                size="small"
                style={{
                    backgroundColor: color,
                    color: '#fff',
                    fontWeight: 600,
                    maxWidth: '100%',
                }}
            />
        </div>
    </Tooltip>
);

interface Props {
    userData: any;
    history: any;
    showToast: (toast: Toast) => void;
}

const ExpiringStockTrackerListScreen: React.FC<Props> = (props) => {
    const { userData, history } = props;
    const { userData: authUser } = userData.userData;
    const isSuperUser = authUser?.is_superuser;
    const userPermissions = authUser?.user_permissions || [];
    const selectedBusiness = userData?.selectedBusiness;
    const businessId = selectedBusiness?.business_id ?? selectedBusiness?.id;

    const [rows, setRows] = useState<ExpiringStockTracker[]>([]);
    const [isLoading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [totalCount, setTotalCount] = useState(0);
    const [statusFilter, setStatusFilter] = useState<string>('ACTIVE');
    const [sortConfig, setSortConfig] = useState<SortConfig | null>({ field: 'activated_at', direction: 'desc' });
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [refreshToken, setRefreshToken] = useState(0);
    const [busyRowId, setBusyRowId] = useState<number | null>(null);

    const loadTrackers = useCallback(() => {
        if (!businessId) {
            setRows([]);
            setTotalCount(0);
            return;
        }
        setLoading(true);
        const orderBy = sortConfig ? `${sortConfig.direction === 'desc' ? '-' : ''}${sortConfig.field}` : undefined;
        getExpiringStockTrackers(businessId, page + 1, pageSize, statusFilter || null, orderBy)
            .catch(handleError)
            .then(checkStatus)
            .then(parseJSON)
            .then((data: PaginatedResponse<ExpiringStockTracker>) => {
                setRows(data.results || []);
                setTotalCount(data.count || 0);
            })
            .catch(() => {
                props.showToast({ message: 'Failed to load expiring stock trackers.', options: ToastError });
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessId, page, pageSize, statusFilter, sortConfig, refreshToken]);

    useEffect(() => {
        loadTrackers();
    }, [loadTrackers]);

    useEffect(() => {
        setPage(0);
    }, [businessId, statusFilter]);

    const refresh = () => setRefreshToken((t) => t + 1);

    const handleSort = (field: string) => {
        setSortConfig((prev) =>
            prev && prev.field === field ? { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' } : { field, direction: 'asc' }
        );
    };

    const handleDeactivate = (tracker: ExpiringStockTracker) => {
        setBusyRowId(tracker.id);
        deactivateExpiringStockTracker(tracker.id)
            .catch(handleError)
            .then(checkStatus)
            .then(parseJSON)
            .then(() => {
                props.showToast({ message: 'Tracker deactivated successfully.', options: ToastSuccess });
                refresh();
            })
            .catch(() => {
                props.showToast({ message: 'Failed to deactivate tracker.', options: ToastError });
            })
            .finally(() => setBusyRowId(null));
    };

    const handleSyncKoronaDeactivate = (tracker: ExpiringStockTracker) => {
        setBusyRowId(tracker.id);
        syncKoronaDeactivateExpiringStockTracker(tracker.id)
            .catch(handleError)
            .then(checkStatus)
            .then(parseJSON)
            .then((data: ExpiringStockTracker & { korona_already_inactive?: boolean }) => {
                props.showToast({
                    message: data.korona_already_inactive
                        ? 'Korona promotion was already inactive.'
                        : 'Korona promotion deactivated successfully.',
                    options: ToastSuccess,
                });
                refresh();
            })
            .catch(() => {
                props.showToast({
                    message: 'Failed to deactivate Korona promotion. Check logs and retry.',
                    options: ToastError,
                });
            })
            .finally(() => setBusyRowId(null));
    };

    const handleArchive = (tracker: ExpiringStockTracker) => {
        setBusyRowId(tracker.id);
        archiveExpiringStockTracker(tracker.id)
            .catch(handleError)
            .then(checkStatus)
            .then(parseJSON)
            .then(() => {
                props.showToast({ message: 'Tracker archived successfully.', options: ToastSuccess });
                refresh();
            })
            .catch(() => {
                props.showToast({ message: 'Failed to archive tracker.', options: ToastError });
            })
            .finally(() => setBusyRowId(null));
    };

    const canDeactivate = hasPermission(userPermissions, 'deactivate_expiringstocktracker', isSuperUser);
    const canArchive = hasPermission(userPermissions, 'archive_expiringstocktracker', isSuperUser);
    const canCreate = hasAddPermission(isSuperUser, userPermissions, PERMISSION_MODEL);

    const columns: ColumnDef<ExpiringStockTracker>[] = [
        {
            id: 'product',
            header: 'Product',
            width: 260,
            render: (row) => (
                <div>
                    <Typography variant="body2">{row.product?.name}</Typography>
                    <Typography variant="caption" color="textSecondary">
                        {row.product?.number}
                    </Typography>
                </div>
            ),
        },
        {
            id: 'location',
            header: 'Location',
            width: 160,
            render: (row) => row.location?.name || '—',
        },
        {
            id: 'initial_expiring_count',
            header: 'Initial Qty',
            width: 110,
            align: 'right',
            sortable: true,
            render: (row) => row.initial_expiring_count,
        },
        {
            id: 'current_remaining_count',
            header: 'Remaining Qty',
            width: 130,
            align: 'right',
            sortable: true,
            render: (row) => row.current_remaining_count,
        },
        {
            id: 'total_sold',
            header: 'Sold',
            width: 90,
            align: 'right',
            render: (row) => row.total_sold ?? 0,
        },
        {
            id: 'promo_name',
            header: 'Promo Name',
            width: 240,
            render: (row) => row.promo_name || '—',
        },
        {
            id: 'status',
            header: 'Tracker Status',
            width: 190,
            render: (row) => (
                <StatusChip
                    label={STATUS_LABELS[row.status] || row.status}
                    color={STATUS_COLORS[row.status] || grey[500]}
                />
            ),
        },
        {
            id: 'promo_status',
            header: 'Promo Status',
            width: 190,
            render: (row) => (
                <StatusChip
                    label={PROMO_STATUS_LABELS[row.promo_status] || row.promo_status}
                    color={PROMO_STATUS_COLORS[row.promo_status] || grey[500]}
                />
            ),
        },
        {
            id: 'product_expiry_date',
            header: 'Tracker Expiry',
            width: 130,
            sortable: true,
            render: (row) =>
                row.product_expiry_date ? moment(row.product_expiry_date).format('YYYY-MM-DD') : '—',
        },
        {
            id: 'activated_at',
            header: 'Activated',
            width: 170,
            sortable: true,
            render: (row) => moment(row.activated_at).format('YYYY-MM-DD HH:mm:ss'),
        },
        {
            id: 'actions',
            header: 'Actions',
            width: 130,
            flex: 0,
            align: 'center',
            render: (row) => {
                if (busyRowId === row.id) {
                    return <CircularProgress size={20} />;
                }
                return (
                    <>
                        {row.status === 'ACTIVE' && canDeactivate && (
                            <Tooltip title="Deactivate tracker">
                                <IconButton size="small" onClick={() => handleDeactivate(row)}>
                                    <Block fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        {EXPIRING_STOCK_DEACTIVATED_STATUSES.includes(row.status) &&
                            canDeactivate &&
                            !!row.promo_korona_uuid && (
                            <Tooltip title="Deactivate Korona promo (orphan cleanup)">
                                <IconButton size="small" onClick={() => handleSyncKoronaDeactivate(row)}>
                                    <Block fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        {EXPIRING_STOCK_DEACTIVATED_STATUSES.includes(row.status) && canArchive && (
                            <Tooltip title="Archive tracker">
                                <IconButton size="small" onClick={() => handleArchive(row)}>
                                    <ArchiveIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </>
                );
            },
        },
    ];

    return (
        <div style={{ width: '100%' }}>
            <CustomTableTitle history={history} title="Expiring Stock Trackers" icon={SVG_ICONS.addTag} />
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 16,
                    flexWrap: 'wrap',
                    gap: 12,
                }}
            >
                <FormControl variant="outlined" size="small" style={{ minWidth: 220 }}>
                    <InputLabel id="expiring-stock-status-filter-label">Status</InputLabel>
                    <Select
                        labelId="expiring-stock-status-filter-label"
                        label="Status"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value as string)}
                    >
                        <MenuItem value="">All statuses</MenuItem>
                        {Object.entries(STATUS_LABELS).map(([value, label]) => (
                            <MenuItem key={value} value={value}>
                                {label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {canCreate && (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<Add />}
                        onClick={() => setCreateDialogOpen(true)}
                        disabled={!businessId}
                    >
                        Create Tracker
                    </Button>
                )}
            </div>
            <DataTable
                columns={columns}
                rows={rows}
                getRowId={(row) => row.id}
                loading={isLoading}
                page={page}
                pageSize={pageSize}
                totalCount={totalCount}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPage(0);
                }}
                sortConfig={sortConfig}
                onSort={handleSort}
                emptyMessage="No expiring stock trackers found."
            />
            {createDialogOpen && (
                <CreateTrackerDialog
                    open={createDialogOpen}
                    businessId={businessId}
                    onClose={() => setCreateDialogOpen(false)}
                    onCreated={() => {
                        setCreateDialogOpen(false);
                        refresh();
                    }}
                    showToast={props.showToast}
                />
            )}
        </div>
    );
};

const mapStateToProps = (state: any) => ({
    userData: state.userLocalData,
});

const mapDispatchToProps = (dispatch: any) => ({
    showToast: (toast: Toast) => dispatch(enqueueSnackbar(toast)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ExpiringStockTrackerListScreen);
